import {EventEmitter, Injectable} from '@angular/core';
import {CognitoHostedUIIdentityProvider} from '@aws-amplify/auth/lib/types/Auth';
import {CognitoUser, ICognitoUserAttributeData} from 'amazon-cognito-identity-js';
import {User} from '../model/auth/User';
import {NotificationHandler} from '../helper/notification.handler';
import {Session} from '../model/auth/Session';
import {ICredentials} from '@aws-amplify/core';
import {Auth} from '@aws-amplify/auth';
import {Role} from '../model/auth/Role';
import {DEFAULT_LOCALE, Locale} from '../model/Locale';
import {LocalStorageService} from 'ngx-webstorage';
import {LocalStorageKeys} from '../helper/local-storage.keys';
import {MatDialog} from '@angular/material/dialog';
import {School, SchoolGrade} from '../model/location/School';
import {Region} from '../model/location/Region';
import {DataHelper} from '../helper/data.helper';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  constructor(private localStorageService: LocalStorageService,
              private dialogRef: MatDialog,) {
  }

  static GOOGLE = CognitoHostedUIIdentityProvider.Google;
  static FACEBOOK = CognitoHostedUIIdentityProvider.Facebook;

  public userChanged = new EventEmitter<never>();

  updateTenantConfig(tenantName?: string, tenantDomain?: string) {
    const config = Auth.configure();
    if (!config.clientMetadata) {
      config.clientMetadata = {};
    }

    const clientMetadata = config.clientMetadata as { [key: string]: string | undefined };

    clientMetadata['tenant_name'] = tenantName;
    clientMetadata['tenant_domain'] = tenantDomain;
  }

  async signIn(username: string, password: string): Promise<CognitoUser> {
    return (await Auth.signIn(username, password)) as CognitoUser;
  }

  async signUp(user: User, password: string, role: Role, locale: Locale): Promise<User> {
    const email = user.email || '';
    const params = {
      username: DataHelper.generateId(),
      password,
      attributes: {
        email: email,
        phone_number: user.phone || '',
        given_name: user.firstName || '',
        family_name: user.lastName || '',
        locale: locale,
        'custom:sign_up_role': role,
      }
    };
    const cognitoUser = await Auth.signUp(params);

    user.id = cognitoUser.user.getUsername();

    return user;
  }

  async completeSignUp(user: CognitoUser, password: string, firstName: string, lastName: string, phone?: string): Promise<User> {
    const params: Record<string, string> = {
      given_name: firstName,
      family_name: lastName
    };
    if (phone) {
      params.phone_number = phone;
    }
    const cognitoUser = await Auth.completeNewPassword(user, password, params) as CognitoUser;

    return User.of(cognitoUser.getUsername());
  }

  async completeNewPassword(user: CognitoUser, password: string): Promise<User> {
    const cognitoUser = await Auth.completeNewPassword(user, password) as CognitoUser;

    return User.of(cognitoUser.getUsername());
  }

  async confirmSignUp(email: string, code: string): Promise<void> {
    await Auth.confirmSignUp(email, code);
  }

  async resetPassword(username: string): Promise<void> {
    await Auth.forgotPassword(username);
  }

  async resetPasswordConfirm(username: string, password: string, code: string): Promise<void> {
    await Auth.forgotPasswordSubmit(username, code, password);
  }

  async resendSignUp(email: string): Promise<string> {
    return (await Auth.resendSignUp(email)) as string;
  }

  async verifyCurrentUserAttribute(attribute: string): Promise<void> {
    await Auth.verifyCurrentUserAttribute(attribute);
  }

  async verifyCurrentUserAttributeConfirm(attribute: string, code: string): Promise<void> {
    await Auth.verifyCurrentUserAttributeSubmit(attribute, code);
  }

  async socialSignIn(provider: CognitoHostedUIIdentityProvider): Promise<ICredentials> {
    return await Auth.federatedSignIn({
      provider
    });
  }

  async logout(): Promise<void> {
    this.dialogRef.closeAll();
    await Auth.signOut();
    this.userChanged.emit();
  }

  async getUser(): Promise<User | null> {
    try {
      const cognitoUser = await AuthService.getCognitoUser();

      return await new Promise((resolve) => {
        cognitoUser.getUserAttributes((err, attributes) => {
          if (err || !attributes) {
            resolve(null);
            return;
          }

          const user = User.of(cognitoUser.getUsername());

          for (const attribute of attributes) {
            if (attribute.getName() === 'email') {
              user.email = attribute.getValue();
            } else if (attribute.getName() === 'preferred_username') {
              user.username = attribute.getValue();
            } else if (attribute.getName() === 'phone_number') {
              user.phone = attribute.getValue();
            } else if (attribute.getName() === 'email_verified') {
              user.emailVerified = attribute.getValue() == 'true';
            } else if (attribute.getName() === 'given_name') {
              user.firstName = attribute.getValue();
            } else if (attribute.getName() === 'family_name') {
              user.lastName = attribute.getValue();
            } else if (attribute.getName() === 'locale') {
              user.locale = attribute.getValue() as Locale;
            } else if (attribute.getName() === 'picture') {
              user.picture = attribute.getValue();
            } else if (attribute.getName() === 'custom:school') {
              user.schoolType = attribute.getValue();
            } else if (attribute.getName() === 'custom:school_id') {
              user.schoolId = attribute.getValue();
            } else if (attribute.getName() === 'custom:school_grade') {
              user.schoolGrade = attribute.getValue() as SchoolGrade;
            } else if (attribute.getName() === 'custom:region') {
              user.region = attribute.getValue();
            } else if (attribute.getName() === 'identities') {
              try {
                const identity = JSON.parse(attribute.getValue()) as Array<object>;
                if (identity.length > 0) {
                  // @ts-ignore
                  user.provider = identity[0].providerType as string;
                }
              } catch (e) {
                console.error('Error parsing user identity provider', e);
              }
            }
          }

          resolve(user);
        });
      });
    } catch (e) {
      return null;
    }
  }

  async updateUserProfile(user: User, updateEmail = false): Promise<boolean> {
    const attributes = [
      {
        Name: 'given_name',
        Value: user.firstName
      },
      {
        Name: 'family_name',
        Value: user.lastName
      },
      {
        Name: 'phone_number',
        Value: user.phone || ''
      },
      {
        Name: 'locale',
        Value: user.locale || DEFAULT_LOCALE
      }
    ];

    if (updateEmail && user.email) {
      attributes.push({
        Name: 'email',
        Value: user.email
      });
    }

    return await this.updateUserProfileInternal(attributes);
  }

  async updateUserSchool(school: School, region: Region, grade?: SchoolGrade): Promise<boolean> {
    const attributes = [
      {
        Name: 'custom:school',
        Value: school.type
      },
      {
        Name: 'custom:school_id',
        Value: school.id
      },
      {
        Name: 'custom:region',
        Value: region.id
      }
    ];

    if (grade) {
      attributes.push({
        Name: 'custom:school_grade',
        Value: grade
      });
    }

    return await this.updateUserProfileInternal(attributes);
  }

  private async updateUserProfileInternal(attributes: ICognitoUserAttributeData[]): Promise<boolean> {
    try {
      const cognitoUser = await AuthService.getCognitoUser();

      return await new Promise<boolean>(resolve => {
        cognitoUser.updateAttributes(attributes, error => {
          if (error) {
            NotificationHandler.handleError($localize`:Notifications|User profile update failed message@@notifications-user-profile-update-failed-error:User profile update failed`, error);
            resolve(false);
          } else {
            this.userChanged.emit();
            resolve(true);
          }
        });
      });
    } catch (e) {
      NotificationHandler.handleError($localize`:Notifications|User profile fetch failed message@@notifications-user-profile-fetch-failed-error:User profile fetch failed`, e);

      return false;
    }
  }

  async changePassword(oldPass: string, newPass: string): Promise<boolean> {
    try {
      const cognitoUser = await AuthService.getCognitoUser();

      return await new Promise<boolean>(resolve => {
        cognitoUser.changePassword(oldPass, newPass, error => {
          if (error) {
            const details = error.message.includes('previousPassword') && error.message.includes('validation')
              ? $localize`:Notifications|Old password is invalid message@@notifications-old-password-invalid-error:Old password is invalid`
              : error.message;
            NotificationHandler.handleError($localize`:Notifications|Change password failed message@@notifications-change-password-failed-error:Change password failed`, details);
            resolve(false);
          } else {
            resolve(true);
          }
        });
      });
    } catch (e) {
      NotificationHandler.handleError($localize`:Notifications|User profile fetch failed message@@notifications-user-profile-fetch-failed-error:User profile fetch failed`, e);

      return false;
    }
  }

  private static async getCognitoUser() {
    return (await Auth.currentAuthenticatedUser({bypassCache: false})) as CognitoUser;
  }

  async getSession(): Promise<Session> {
    let session: Session;

    try {
      const cognitoSession = await Auth.currentSession();

      const groups = cognitoSession.getAccessToken().payload['cognito:groups'] as string[] || [];
      const roles = groups.map((group: string) => group as Role);
      const role = this.localStorageService.retrieve(LocalStorageKeys.SESSION_ROLE) as Role || null;

      session = new Session(roles, cognitoSession.getIdToken().getJwtToken(), role);
    } catch (_) {
      session = new Session([], undefined, undefined);
    }

    return session;
  }

}
